import { useEffect, useRef } from 'react'
import { useAuthStore } from '../store/authStore.js'
import { api } from '../api/client.js'
import { loginPathFor } from '../auth/loginPath.js'
import { installTabSessionLifecycle } from '../auth/sessionLifecycle.js'

/**
 * Validates the current session on app startup.
 * If the server rejects the session (401), clears auth and agent state.
 * Also sets up a periodic heartbeat to detect server restarts.
 */
export function useSessionValidation() {
  const user = useAuthStore((s) => s.user)
  const clearSession = useAuthStore((s) => s.clearSession)
  const hasValidated = useRef(false)

  // Validate session on startup
  useEffect(() => {
    // Only validate once on initial load if we have a persisted user
    if (!user || hasValidated.current) return
    hasValidated.current = true

    // Platform admin tokens have aud=platform and are rejected by tenant
    // routes (e.g. /agent/break-codes). Skip validation for platform users —
    // their access token is short-lived (2h, no refresh) and will expire
    // naturally, at which point the next API call returns 401 and redirects.
    const isPlatformUser =
      user.isSuperAdmin ||
      user.roles?.some((r) => r === 'PLATFORM_ADMIN' || r === 'PLATFORM_SUPER_ADMIN')
    if (isPlatformUser) return

    async function validate() {
      try {
        // Try to fetch a lightweight endpoint that requires auth
        // /api/agent/break-codes is a good candidate (lightweight, agent-specific)
        await api.get('/agent/break-codes')
        console.log('[Session] Validated - session is active')
      } catch (err: unknown) {
        const apiErr = err as { status?: number }
        if (apiErr.status === 401) {
          console.warn('[Session] Server rejected session (401) - clearing auth state')
          clearSession()
          window.location.href = loginPathFor(window.location.pathname)
        } else {
          // Network error or server down - could be temporary
          console.warn('[Session] Validation failed (network/server error):', err)
        }
      }
    }

    validate()
  }, [user, clearSession])

  // Heartbeat: periodically check if server is still alive
  useEffect(() => {
    if (!user) return

    const HEARTBEAT_INTERVAL = 30_000 // 30 seconds
    let missedBeats = 0
    const MAX_MISSED_BEATS = 2

    const heartbeat = setInterval(async () => {
      try {
        const res = await fetch('/api/health', {
          method: 'GET',
          credentials: 'include',
        })
        if (res.ok) {
          missedBeats = 0
        } else if (res.status === 401) {
          console.warn('[Session] Heartbeat received 401 - server restarted or session expired')
          clearSession()
          window.location.href = loginPathFor(window.location.pathname)
        }
      } catch {
        missedBeats++
        console.warn(`[Session] Heartbeat failed (${missedBeats}/${MAX_MISSED_BEATS})`)
        if (missedBeats >= MAX_MISSED_BEATS) {
          console.warn('[Session] Server unreachable - clearing session')
          clearSession()
          window.location.href = loginPathFor(window.location.pathname)
        }
      }
    }, HEARTBEAT_INTERVAL)

    return () => clearInterval(heartbeat)
  }, [user, clearSession])

  // Tab close / refresh signs out (kcv2 parity), unless another signed-in tab
  // of the app is still open; another tab switching user is followed here.
  // A refresh logging out is now the product requirement (see main.tsx
  // logoutOnReload), which is why the old objection to an unload logout — that
  // it could not tell a refresh from a close — no longer applies.
  useEffect(() => (user ? installTabSessionLifecycle() : undefined), [Boolean(user)])
}
